/**
 * SSM Parameter Service
 * Fetches configuration parameters from AWS SSM Parameter Store
 */

export interface BannerConfig {
  text: string
  /** Shorter text shown on phone-sized screens when set; desktop/tablet show text. If unset, text is shown (truncated on small screens). */
  shortText?: string
  /** Banner background color (defaults by type). Use rgba() or 8-digit hex for transparency. */
  color?: string
  textColor?: string
  /** Background opacity 0–1. Applied on top of color. */
  opacity?: number
  /** Text opacity 0–1. */
  textOpacity?: number
  type?: 'info' | 'warning' | 'error'
  /** Whether to show the close button (default: true). */
  close?: boolean
  disabled?: boolean  // When true, banner is disabled (placeholder value)
  linkUrl?: string    // Optional URL to make the banner text clickable
  /** Auto-hide after this many seconds. Omit or 0 = no auto-hide. */
  autoHide?: number
}

export interface LoginLockdownConfig {
  enabled: boolean
  message?: string
  redirectUrl?: string
}

export interface EmergencyRedirectConfig {
  enabled: boolean
  redirectUrl: string
  message?: string
}

export interface SSMConfig {
  /** Banner for logged-in users */
  loggedinBanner?: BannerConfig
  /** Banner for non-logged-in users */
  loggedoutBanner?: BannerConfig
  /** Login lockdown configuration */
  loginLockdown?: LoginLockdownConfig
  /** Emergency redirect configuration */
  emergencyRedirect?: EmergencyRedirectConfig
}

let cached: SSMConfig | null = null
let cacheTime = 0
const CACHE_DURATION_MS = 60000 // 1 minute cache

function ssmEndpoint(): string {
  const API_BASE = import.meta.env.VITE_API_URL ?? ''
  if (API_BASE.startsWith('http')) {
    return `${API_BASE.replace(/\/$/, '')}/ssm-config`
  }
  const base = window.location.origin + (import.meta.env.BASE_URL ?? '').replace(/\/$/, '')
  return `${base}/api/ssm-config`
}

export async function fetchSSMConfig(): Promise<SSMConfig> {
  const now = Date.now()
  if (cached && (now - cacheTime) < CACHE_DURATION_MS) {
    return cached
  }

  try {
    const url = ssmEndpoint()
    const res = await fetch(url)
    if (!res.ok) {
      console.warn(`SSM config fetch failed: ${res.status}`)
      return {}
    }
    const data = await res.json()
    cached = data
    cacheTime = now
    return data
  } catch (e) {
    console.warn('Could not load SSM config from API', e)
    return {}
  }
}

/** Get the banner config for logged-in users */
export function getLoggedInBanner(): BannerConfig | undefined {
  const banner = cached?.loggedinBanner
  // Return undefined if banner is disabled (placeholder value)
  if (banner && banner.disabled) return undefined
  return banner
}

/** Get the banner config for non-logged-in users */
export function getLoggedOutBanner(): BannerConfig | undefined {
  const banner = cached?.loggedoutBanner
  // Return undefined if banner is disabled (placeholder value)
  if (banner && banner.disabled) return undefined
  return banner
}

/** Check if login lockdown is enabled */
export function isLoginLockdownEnabled(): boolean {
  return cached?.loginLockdown?.enabled ?? false
}

/** Get the login lockdown message */
export function getLoginLockdownMessage(): string | undefined {
  return cached?.loginLockdown?.message
}

/** Get the emergency redirect URL */
export function getEmergencyRedirectUrl(): string | undefined {
  return cached?.emergencyRedirect?.redirectUrl
}

/** Check if emergency redirect is enabled */
export function isEmergencyRedirectEnabled(): boolean {
  return cached?.emergencyRedirect?.enabled ?? false
}